import { useCallback, useEffect, useRef, useState } from 'react';

/** Runs `callback` once the caller has been quiet for `delayMs`. */
export function useDebouncedCallback<Args extends unknown[]>(
  callback: (...args: Args) => void,
  delayMs: number,
): { run: (...args: Args) => void; cancel: () => void; flush: () => void } {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingArgs = useRef<Args | null>(null);
  const latest = useRef(callback);

  useEffect(() => {
    latest.current = callback;
  }, [callback]);

  const cancel = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    pendingArgs.current = null;
  }, []);

  const flush = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    const args = pendingArgs.current;
    pendingArgs.current = null;
    if (args) latest.current(...args);
  }, []);

  const run = useCallback(
    (...args: Args) => {
      pendingArgs.current = args;
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        const queued = pendingArgs.current;
        pendingArgs.current = null;
        if (queued) latest.current(...queued);
      }, delayMs);
    },
    [delayMs],
  );

  useEffect(() => cancel, [cancel]);

  return { run, cancel, flush };
}

/** Tracks connectivity so the editor can tell the user why saving paused. */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  return online;
}

/** Warns before a reload or navigation would discard unsaved work. */
export function useUnsavedChangesWarning(hasUnsavedChanges: boolean): void {
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);
}

/**
 * A small preference that outlives the page: held in React state, mirrored to
 * `localStorage` so the next load starts where the last one left off.
 *
 * Anything read back out of storage can be wrong — hand-edited, or left over
 * from a version that spelled the options differently — so a value that is not
 * one of `allowed` is dropped for `fallback` rather than trusted into the UI.
 * Storage can also refuse outright (a browser told to block site data, or
 * Safari's private mode), and remembering a preference is never worth failing
 * a page over, so both directions fall back to ordinary in-memory state.
 */
export function useStoredPreference<T extends string>(
  key: string,
  allowed: readonly T[],
  fallback: T,
): [T, (next: T) => void] {
  const storageKey = `cqb:${key}`;

  const [value, setValue] = useState<T>(() => {
    try {
      const stored = window.localStorage.getItem(storageKey);
      return allowed.find((option) => option === stored) ?? fallback;
    } catch {
      return fallback;
    }
  });

  const store = useCallback(
    (next: T) => {
      setValue(next);
      try {
        window.localStorage.setItem(storageKey, next);
      } catch {
        // Remembered for this page only. Nothing the user asked for has
        // failed, so nothing is said about it.
      }
    },
    [storageKey],
  );

  return [value, store];
}

/**
 * Whether a CSS media query matches right now, as a value React can branch on.
 *
 * For the handful of places where a layout is not only STYLED differently at a
 * breakpoint but SHAPED differently — where the same component belongs in a
 * different part of the tree on a phone than on a desk. Tailwind covers the
 * rest, and should: hiding one copy with `lg:hidden` and showing another is
 * free, whereas this re-renders. What it is for is the case where rendering
 * both copies is not free because each copy is a live thing — a thread that
 * polls, marks itself read and holds a half-typed reply — and mounting two of
 * them means two of all of that.
 *
 * Reads the answer synchronously so the first paint is already right; a
 * browser without `matchMedia` (jsdom, mostly) reports no match, which lands
 * on the narrow layout the same way an unstyled page does.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia?.(query)?.matches ?? false);

  useEffect(() => {
    const list = window.matchMedia?.(query);
    if (!list) return;
    // Re-read on subscribe: rotating a phone between the initial render and
    // this effect would otherwise leave the answer a breakpoint behind.
    setMatches(list.matches);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/** Sets the document title, restoring the previous one on unmount. */
export function useDocumentTitle(title: string): void {
  useEffect(() => {
    const previous = document.title;
    document.title = `${title} · BlastoClean`;
    return () => {
      document.title = previous;
    };
  }, [title]);
}

/**
 * A value that settles `delayMs` after the caller stops changing it.
 *
 * For the search boxes that turn keystrokes into requests. Distinct from
 * `useDebouncedCallback` above and not a replacement for it: that one delays an
 * ACTION, this one delays a VALUE — which is what a `useQuery` key needs, since
 * a query keyed on every keystroke refetches on every keystroke however the
 * callback around it is throttled.
 */
export function useDebouncedValue<T>(value: T, delayMs = 300): T {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return settled;
}
